import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, Calendar, User, ArrowLeft, Tag, Share2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { publicApi } from "@/lib/api";
import { toast } from "sonner";

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    fetchPost();
    window.scrollTo(0, 0);
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const response = await publicApi.get(`/blog/${slug}`);
      setPost(response.data);
      
      // Fetch related posts
      const allPosts = await publicApi.get("/blog");
      const relatedPosts = allPosts.data
        .filter((p) => p.slug !== slug && p.category === response.data.category)
        .slice(0, 3);
      setRelated(relatedPosts);
    } catch (error) {
      console.error("Error fetching post:", error);
      toast.error("Blog post not found");
      navigate("/blog");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: post.title, text: post.excerpt, url });
      } catch (e) {
        // Cancelled
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div data-testid="blog-detail-page" className="grain-texture min-h-screen bg-background">
      <SEO
        title={post.title}
        description={post.excerpt || post.title}
        path={`/blog/${post.slug}`}
        image={post.image_url}
        type="article"
        keywords={`${post.category}, andaman ${post.category?.toLowerCase()}, ${post.title}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.excerpt,
          image: post.image_url,
          author: { "@type": "Person", name: post.author || "Andaman Avalon" },
          publisher: {
            "@type": "Organization",
            name: "Andaman Avalon",
            logo: {
              "@type": "ImageObject",
              url: "https://customer-assets-jai6qajn.emergentagent.net/job_031c01f6-1122-4ae8-87ee-69acab3ae0f1/artifacts/kw4zjnmy_ava.jpeg",
            },
          },
          datePublished: post.published_at,
          mainEntityOfPage: `https://www.andamanavalon.com/blog/${post.slug}`,
        }}
      />
      <Navbar />

      {/* Hero Image */}
      <section className="relative h-[50vh] mt-20">
        <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
        <div className="hero-overlay absolute inset-0"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-4 max-w-4xl">
            <span className="accent-label text-secondary mb-4 block">{post.category}</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold mb-4">
              {post.title}
            </h1>
            <div className="flex items-center justify-center gap-4 text-white/80 text-sm">
              <span className="flex items-center">
                <User size={14} className="mr-1" />
                {post.author}
              </span>
              <span>·</span>
              <span className="flex items-center">
                <Calendar size={14} className="mr-1" />
                {formatDate(post.published_at)}
              </span>
              <span>·</span>
              <span className="flex items-center">
                <Clock size={14} className="mr-1" />
                {post.read_time} min read
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Actions */}
          <div className="flex items-center justify-between mb-8">
            <Button
              onClick={() => navigate("/blog")}
              variant="ghost"
              data-testid="back-to-blog-btn"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Blog
            </Button>
            <Button onClick={handleShare} variant="outline" data-testid="share-btn">
              <Share2 size={16} className="mr-2" />
              Share
            </Button>
          </div>

          {/* Excerpt */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xl text-muted-foreground leading-relaxed mb-8 font-serif italic border-l-4 border-secondary pl-6"
          >
            {post.excerpt}
          </motion.p>

          {/* Content (HTML) */}
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="prose prose-lg max-w-none blog-content"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-border">
              <div className="flex items-center flex-wrap gap-2">
                <Tag size={16} className="text-muted-foreground mr-1" />
                {post.tags.map((tag, i) => (
                  <span
                    key={i}
                    data-testid={`blog-tag-${i}`}
                    className="text-sm bg-muted text-primary px-3 py-1 rounded-full font-medium"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-12 p-8 bg-primary text-primary-foreground rounded-xl text-center">
            <h3 className="text-2xl font-serif mb-3 text-white">Ready to Experience Andaman?</h3>
            <p className="text-white/80 mb-6">Let us craft your perfect island getaway</p>
            <Link to="/packages" data-testid="cta-explore-packages" className="btn-secondary inline-block">
              Explore Packages
            </Link>
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {related.length > 0 && (
        <section className="py-16 bg-muted">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-serif mb-8">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((r, index) => (
                <Link
                  key={r.id}
                  to={`/blog/${r.slug}`}
                  data-testid={`related-post-${index}`}
                  className="package-card bg-white rounded-lg overflow-hidden border border-border/50 shadow-sm group"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={r.image_url}
                      alt={r.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <span className="text-xs bg-muted text-primary px-2 py-1 rounded font-semibold uppercase">
                      {r.category}
                    </span>
                    <h3 className="text-lg font-serif mt-3 group-hover:text-secondary transition-colors line-clamp-2">
                      {r.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                      {r.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default BlogDetail;
